import type { Keyframe } from "../types/timeline";

/**
 * Motion path authoring model, and the one place its conventions are defined.
 *
 * COORDINATES: `x` and `y` are canvas pixels measured from the centre of the
 * frame, the space `Clip.transform.position` uses on timeline clips (the
 * renderers draw at `canvasWidth / 2 + position.x`). Baking a path into
 * `position.x` / `position.y` keyframes is a pass through: no scaling, no
 * origin shift. Text, shape, SVG, and sticker clips store `transform.position`
 * as a 0..1 fraction of the frame instead (title-engine and graphics-engine
 * multiply by width and height), and they live outside `timeline.tracks`, so
 * motion paths are offered on timeline clips only rather than carrying two
 * meanings in one field.
 *
 * TIME: `time` is seconds measured from the clip's own start, the clock every
 * keyframe on a clip already uses (renderers evaluate at
 * `time - clip.startTime`). Seconds rather than a 0..1 fraction means trimming
 * a clip does not silently remap a path: it behaves exactly like the keyframes
 * it bakes into.
 *
 * EVALUATION: a path is authoring state and no renderer reads it.
 * `applyMotionPathToKeyframes` bakes it into ordinary keyframes, which preview,
 * export, undo, and the project file already understand. That keeps a path from
 * becoming a second animation system that only one panel can see.
 *
 * OWNERSHIP: while a path is enabled it owns `position.x` and `position.y` on
 * its clip (and `rotation` when `autoOrient` is on). Baked keyframes carry the
 * `motion-path:` id prefix so re-baking replaces exactly the previous bake and
 * leaves hand authored keyframes for other properties untouched.
 */

export interface ClipMotionPathControlPoints {
  cp1: { x: number; y: number };
  cp2: { x: number; y: number };
}

export interface ClipMotionPathPoint {
  x: number;
  y: number;
  /** Seconds from the clip's start. */
  time: number;
  controlPoints?: ClipMotionPathControlPoints;
}

export type MotionPathType = "linear" | "bezier" | "catmull-rom";

export interface ClipMotionPath {
  readonly enabled: boolean;
  readonly pathType: MotionPathType;
  readonly points: readonly ClipMotionPathPoint[];
  /** Rotate the clip to face along the path, baked as `rotation` keyframes. */
  readonly autoOrient: boolean;
}

export interface MotionPathSample {
  readonly time: number;
  readonly x: number;
  readonly y: number;
  /** Tangent direction in degrees, clockwise from the positive x axis. */
  readonly rotation: number;
}

export const MOTION_PATH_KEYFRAME_ID_PREFIX = "motion-path:";

/**
 * Samples taken across each curved segment when baking. Keyframes interpolate
 * linearly in time between samples, so this is the only knob deciding how
 * closely the rendered motion follows the curve. Straight (`linear`) paths bake
 * one keyframe per point and ignore it.
 */
const CURVE_SAMPLES_PER_SEGMENT = 8;

const CATMULL_ROM_TENSION = 0.5;

const RADIANS_TO_DEGREES = 180 / Math.PI;

export function isMotionPathKeyframe(keyframe: Keyframe): boolean {
  return keyframe.id.startsWith(MOTION_PATH_KEYFRAME_ID_PREFIX);
}

/**
 * Properties a path takes over while it is enabled. Callers that write a path
 * must fold any existing user keyframes for these properties into the path
 * first (see `motionPathPointsFromKeyframes`), because the bake replaces them.
 */
export function motionPathDrivenProperties(
  path: ClipMotionPath | undefined,
): string[] {
  if (!path?.enabled) return [];
  return path.autoOrient
    ? ["position.x", "position.y", "rotation"]
    : ["position.x", "position.y"];
}

function sortByTime(points: readonly ClipMotionPathPoint[]): ClipMotionPathPoint[] {
  return [...points].sort((a, b) => a.time - b.time);
}

function cubicBezierPoint(
  p0: { x: number; y: number },
  cp1: { x: number; y: number },
  cp2: { x: number; y: number },
  p1: { x: number; y: number },
  t: number,
): { x: number; y: number } {
  const oneMinusT = 1 - t;
  const oneMinusT2 = oneMinusT * oneMinusT;
  const oneMinusT3 = oneMinusT2 * oneMinusT;
  const t2 = t * t;
  const t3 = t2 * t;

  return {
    x:
      oneMinusT3 * p0.x +
      3 * oneMinusT2 * t * cp1.x +
      3 * oneMinusT * t2 * cp2.x +
      t3 * p1.x,
    y:
      oneMinusT3 * p0.y +
      3 * oneMinusT2 * t * cp1.y +
      3 * oneMinusT * t2 * cp2.y +
      t3 * p1.y,
  };
}

function bezierSegmentPoint(
  p0: ClipMotionPathPoint,
  p1: ClipMotionPathPoint,
  t: number,
): { x: number; y: number } {
  if (p0.controlPoints && p1.controlPoints) {
    return cubicBezierPoint(
      p0,
      p0.controlPoints.cp2,
      p1.controlPoints.cp1,
      p1,
      t,
    );
  }

  return {
    x: p0.x + (p1.x - p0.x) * t,
    y: p0.y + (p1.y - p0.y) * t,
  };
}

/**
 * Catmull-Rom across the segment starting at `index`, honouring each point's
 * own time. The neighbours that shape the tangents are clamped at the ends.
 */
function catmullRomSegmentPoint(
  points: ClipMotionPathPoint[],
  index: number,
  t: number,
): { x: number; y: number } {
  const p0 = points[Math.max(0, index - 1)];
  const p1 = points[index];
  const p2 = points[index + 1];
  const p3 = points[Math.min(points.length - 1, index + 2)];

  const t2 = t * t;
  const t3 = t2 * t;

  const m1x = CATMULL_ROM_TENSION * (p2.x - p0.x);
  const m1y = CATMULL_ROM_TENSION * (p2.y - p0.y);
  const m2x = CATMULL_ROM_TENSION * (p3.x - p1.x);
  const m2y = CATMULL_ROM_TENSION * (p3.y - p1.y);

  const a = 2 * t3 - 3 * t2 + 1;
  const b = t3 - 2 * t2 + t;
  const c = -2 * t3 + 3 * t2;
  const d = t3 - t2;

  return {
    x: a * p1.x + b * m1x + c * p2.x + d * m2x,
    y: a * p1.y + b * m1y + c * p2.y + d * m2y,
  };
}

/**
 * The one place a path type turns into a position. Everything that draws or
 * bakes a path goes through here, so an editor marker cannot land somewhere the
 * render does not.
 */
function segmentPoint(
  pathType: MotionPathType,
  points: ClipMotionPathPoint[],
  index: number,
  t: number,
): { x: number; y: number } {
  const from = points[index];
  const to = points[index + 1];

  switch (pathType) {
    case "linear":
      return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
    case "bezier":
      return bezierSegmentPoint(from, to, t);
    case "catmull-rom":
      return catmullRomSegmentPoint(points, index, t);
  }
}

/**
 * Positions along the path, in clip-local seconds. Curved paths are sampled;
 * straight ones keep exactly the points the user placed.
 */
export function sampleMotionPathCurve(
  path: ClipMotionPath,
): MotionPathSample[] {
  const points = sortByTime(path.points);
  if (points.length < 2) return [];

  const positions: Array<{ time: number; x: number; y: number }> = [];

  if (path.pathType === "linear") {
    for (const point of points) {
      positions.push({ time: point.time, x: point.x, y: point.y });
    }
  } else {
    for (let i = 0; i < points.length - 1; i += 1) {
      const from = points[i];
      const to = points[i + 1];
      // Segments after the first skip step 0: it is the previous segment's
      // end point and would bake a duplicate keyframe at the same time.
      for (
        let step = i === 0 ? 0 : 1;
        step <= CURVE_SAMPLES_PER_SEGMENT;
        step += 1
      ) {
        const t = step / CURVE_SAMPLES_PER_SEGMENT;
        const position = segmentPoint(path.pathType, points, i, t);
        positions.push({
          time: from.time + (to.time - from.time) * t,
          x: position.x,
          y: position.y,
        });
      }
    }
  }

  let lastRotation = 0;
  return positions.map((position, index) => {
    const from = index < positions.length - 1 ? position : positions[index - 1];
    const to = index < positions.length - 1 ? positions[index + 1] : position;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (dx !== 0 || dy !== 0) {
      lastRotation = Math.atan2(dy, dx) * RADIANS_TO_DEGREES;
    }
    return { ...position, rotation: lastRotation };
  });
}

function bakedKeyframe(
  property: string,
  index: number,
  time: number,
  value: number,
): Keyframe {
  return {
    id: `${MOTION_PATH_KEYFRAME_ID_PREFIX}${property}:${index}`,
    time,
    property,
    value,
    // The curve shape already lives in the sampled positions, so the segments
    // between them must not be eased a second time.
    easing: "linear",
  };
}

export function bakeMotionPathKeyframes(
  path: ClipMotionPath | undefined,
): Keyframe[] {
  if (!path?.enabled) return [];

  const samples = sampleMotionPathCurve(path);
  if (samples.length < 2) return [];

  const keyframes: Keyframe[] = [];
  samples.forEach((sample, index) => {
    keyframes.push(bakedKeyframe("position.x", index, sample.time, sample.x));
    keyframes.push(bakedKeyframe("position.y", index, sample.time, sample.y));
    if (path.autoOrient) {
      keyframes.push(
        bakedKeyframe("rotation", index, sample.time, sample.rotation),
      );
    }
  });

  return keyframes;
}

/**
 * The clip's keyframe list with `path` baked in. Dropping the path (undefined,
 * disabled, or fewer than two points) strips the previous bake and leaves every
 * hand authored keyframe alone.
 */
export function applyMotionPathToKeyframes(
  existing: readonly Keyframe[],
  path: ClipMotionPath | undefined,
): Keyframe[] {
  const baked = bakeMotionPathKeyframes(path);
  const driven = new Set(baked.length > 0 ? motionPathDrivenProperties(path) : []);

  const kept = existing.filter(
    (keyframe) =>
      !isMotionPathKeyframe(keyframe) && !driven.has(keyframe.property),
  );

  return [...kept, ...baked].sort((a, b) => a.time - b.time);
}

/**
 * Path points recovered from a clip's existing position keyframes, so enabling
 * a path adopts the motion the user already authored instead of discarding it.
 * Only times carrying both an x and a y produce a point.
 */
export function motionPathPointsFromKeyframes(
  keyframes: readonly Keyframe[],
): ClipMotionPathPoint[] {
  const byTime = new Map<number, { x?: number; y?: number }>();

  for (const keyframe of keyframes) {
    if (
      keyframe.property !== "position.x" &&
      keyframe.property !== "position.y"
    ) {
      continue;
    }
    if (typeof keyframe.value !== "number") continue;

    const entry = byTime.get(keyframe.time) ?? {};
    if (keyframe.property === "position.x") {
      entry.x = keyframe.value;
    } else {
      entry.y = keyframe.value;
    }
    byTime.set(keyframe.time, entry);
  }

  const points: ClipMotionPathPoint[] = [];
  for (const [time, position] of byTime) {
    if (position.x === undefined || position.y === undefined) continue;
    points.push({ x: position.x, y: position.y, time });
  }

  return generateDefaultControlPoints(points.sort((a, b) => a.time - b.time));
}

/**
 * Bezier handles pointing along the neighbouring points, so a freshly placed
 * point curves smoothly instead of snapping to a corner.
 */
export function generateDefaultControlPoints(
  points: readonly ClipMotionPathPoint[],
): ClipMotionPathPoint[] {
  if (points.length < 2) return [...points];

  const sortedPoints = sortByTime(points);

  return sortedPoints.map((point, i) => {
    if (point.controlPoints) return point;

    const prev = sortedPoints[i - 1];
    const next = sortedPoints[i + 1];

    let tangentX = 0;
    let tangentY = 0;

    if (prev && next) {
      tangentX = (next.x - prev.x) / 4;
      tangentY = (next.y - prev.y) / 4;
    } else if (next) {
      tangentX = (next.x - point.x) / 3;
      tangentY = (next.y - point.y) / 3;
    } else if (prev) {
      tangentX = (point.x - prev.x) / 3;
      tangentY = (point.y - prev.y) / 3;
    }

    return {
      ...point,
      controlPoints: {
        cp1: { x: point.x - tangentX, y: point.y - tangentY },
        cp2: { x: point.x + tangentX, y: point.y + tangentY },
      },
    };
  });
}

/** SVG `d` for the path, used by the on canvas overlay. */
export function generateBezierPath(points: readonly ClipMotionPathPoint[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  const sortedPoints = sortByTime(points);
  let path = `M ${sortedPoints[0].x} ${sortedPoints[0].y}`;

  for (let i = 0; i < sortedPoints.length - 1; i += 1) {
    const p0 = sortedPoints[i];
    const p1 = sortedPoints[i + 1];

    if (p0.controlPoints && p1.controlPoints) {
      path += ` C ${p0.controlPoints.cp2.x} ${p0.controlPoints.cp2.y}, ${p1.controlPoints.cp1.x} ${p1.controlPoints.cp1.y}, ${p1.x} ${p1.y}`;
    } else {
      const cp1x = p0.x + (p1.x - p0.x) / 3;
      const cp1y = p0.y + (p1.y - p0.y) / 3;
      const cp2x = p0.x + (2 * (p1.x - p0.x)) / 3;
      const cp2y = p0.y + (2 * (p1.y - p0.y)) / 3;
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p1.x} ${p1.y}`;
    }
  }

  return path;
}

/** Position on the path at a clip-local time, for editors and hit testing. */
export function sampleClipMotionPath(
  path: ClipMotionPath,
  time: number,
): { x: number; y: number } | null {
  const sortedPoints = sortByTime(path.points);
  if (sortedPoints.length === 0) return null;
  if (sortedPoints.length === 1) {
    return { x: sortedPoints[0].x, y: sortedPoints[0].y };
  }

  if (time <= sortedPoints[0].time) {
    return { x: sortedPoints[0].x, y: sortedPoints[0].y };
  }

  const last = sortedPoints[sortedPoints.length - 1];
  if (time >= last.time) {
    return { x: last.x, y: last.y };
  }

  let startIdx = 0;
  for (let i = 0; i < sortedPoints.length - 1; i += 1) {
    if (time >= sortedPoints[i].time && time <= sortedPoints[i + 1].time) {
      startIdx = i;
      break;
    }
  }

  const p0 = sortedPoints[startIdx];
  const p1 = sortedPoints[startIdx + 1];
  const segmentDuration = p1.time - p0.time;
  const t = segmentDuration > 0 ? (time - p0.time) / segmentDuration : 0;

  return segmentPoint(path.pathType, sortedPoints, startIdx, t);
}
